/**
 * Design tokens — one complete visual system for one colour mode.
 *
 * Ported from the Cognultsoft Fabrica theme system. A preset is a small seed
 * (see `deriveTokens.ts`); everything here is computed from it, so the
 * relationships between values stay consistent across presets.
 */

/** A colour ramp. Every brand role carries the same five stops plus a readable foreground. */
export interface ColorRamp {
  lighter: string;
  light: string;
  main: string;
  dark: string;
  darker: string;
  /** Text that is legible on `main`. */
  contrastText: string;
}

/** Eleven neutral stops, lightest to darkest, tinted towards the preset's neutral hue. */
export interface NeutralScale {
  n0: string;
  n50: string;
  n100: string;
  n200: string;
  n300: string;
  n400: string;
  n500: string;
  n600: string;
  n700: string;
  n800: string;
  n900: string;
}

/** Meaning, not decoration — status colours never follow the brand hue. */
export interface StatusColors {
  info: ColorRamp;
  success: ColorRamp;
  warning: ColorRamp;
  error: ColorRamp;
  /** Neutral/idle states: drafts, disabled pills. */
  neutral: ColorRamp;
}

/** Where content sits, ordered by how far forward the surface reads. */
export interface SurfaceTokens {
  /** The page behind everything. */
  canvas: string;
  /** Cards, panels, tables. */
  paper: string;
  /** Above paper: menus, popovers, drawers. */
  raised: string;
  /** Below paper: wells, code blocks. */
  sunken: string;
  /** The backdrop behind a modal, including its alpha. */
  scrim: string;
}

export interface TextTokens {
  primary: string;
  secondary: string;
  disabled: string;
  /** Legible on a brand-coloured or dark chrome surface. */
  inverse: string;
  link: string;
}

export interface BorderTokens {
  /** Hairlines between rows and inside cards. */
  subtle: string;
  /** Card and input outlines. */
  default: string;
  /** Emphasis: a selected cell, an active tab. */
  strong: string;
  /** The focus ring. */
  focus: string;
}

/**
 * Interaction overlays as complete colours rather than alphas, so a hovered
 * row looks the same whichever surface it sits on.
 */
export interface StateTokens {
  hover: string;
  selected: string;
  selectedHover: string;
  pressed: string;
  disabledBg: string;
}

export interface ElevationTokens {
  e0: string;
  e1: string;
  e2: string;
  e3: string;
  e4: string;
}

export interface RadiusTokens {
  /** Chips, badges, small inputs. */
  sm: number;
  /** The default: cards, buttons, inputs. */
  md: number;
  /** Panels, drawers, dialogs. */
  lg: number;
  /** Fully rounded. */
  pill: number;
}

export interface TypographyTokens {
  fontFamily: string;
  /** Ids, codes, keys — anywhere alignment carries meaning. */
  monoFamily: string;
  /** Base size in px. */
  baseSize: number;
  /** Body line height as a unitless multiple. */
  lineHeight: number;
  weightRegular: number;
  weightMedium: number;
  weightBold: number;
  /** Heading letter spacing in px. */
  headingLetterSpacing: number;
}

export interface NavTokens {
  /** Top bar background. */
  background: string;
  text: string;
  activeText: string;
  activeIndicator: string;
  border: string;
}

export interface SidebarTokens {
  background: string;
  text: string;
  /** Group headings and inactive icons. */
  mutedText: string;
  activeBackground: string;
  activeText: string;
  hoverBackground: string;
  border: string;
}

export interface DrawerTokens {
  headerBackground: string;
  headerText: string;
  background: string;
  footerBackground: string;
  border: string;
}

export interface TableTokens {
  headerBackground: string;
  headerText: string;
  rowBackground: string;
  /** Zebra striping. Equal to rowBackground on presets that do not stripe. */
  rowAltBackground: string;
  rowHover: string;
  rowSelected: string;
  border: string;
}

export interface InputTokens {
  background: string;
  text: string;
  placeholder: string;
  border: string;
  hoverBorder: string;
  focusBorder: string;
  disabledBackground: string;
  errorBorder: string;
}

export interface ButtonTokens {
  /** Filled buttons. */
  primaryBackground: string;
  primaryText: string;
  primaryHover: string;
  /** Outlined buttons. */
  secondaryBorder: string;
  secondaryText: string;
  secondaryHover: string;
  /** Text buttons. */
  ghostText: string;
  ghostHover: string;
  radius: number;
}

/** One complete visual system, for one mode. */
export interface ThemeTokens {
  brand: {
    primary: ColorRamp;
    secondary: ColorRamp;
    accent: ColorRamp;
  };
  neutral: NeutralScale;
  status: StatusColors;
  surface: SurfaceTokens;
  text: TextTokens;
  border: BorderTokens;
  state: StateTokens;
  elevation: ElevationTokens;
  radius: RadiusTokens;
  typography: TypographyTokens;
  component: {
    nav: NavTokens;
    sidebar: SidebarTokens;
    drawer: DrawerTokens;
    table: TableTokens;
    input: InputTokens;
    button: ButtonTokens;
  };
}

export type ThemeCategory = 'Camptain' | 'Enterprise' | 'Premium Dark' | 'High-Contrast';

export interface ThemePresetMeta {
  /** Stable key. Persisted, so it never changes once shipped. */
  id: string;
  name: string;
  category: ThemeCategory;
  /** One line saying what the preset is for. */
  description: string;
  /** The mode the preset was designed in. The other mode is still supported. */
  designedFor: 'light' | 'dark';
  /** Ordering within a category. */
  sortOrder: number;
}

/** A preset carries both modes, so the light/dark toggle works on every preset. */
export interface ThemePreset {
  meta: ThemePresetMeta;
  light: ThemeTokens;
  dark: ThemeTokens;
}

/** The preset the app falls back to. */
export const DEFAULT_PRESET_ID = 'camptain-indigo';
